import type { UIEvent } from 'react'
import { useCallback, useMemo, useRef, useState } from 'react'
import type { LegalDocument } from '@/shared/domain'
import { useSubmitGuard } from '@/shared/ui/hooks'

export type UseLegalAcceptanceModalParams = {
  documents: LegalDocument[]
  onAccept: (documents: LegalDocument[]) => Promise<void>
}

export type UseLegalAcceptanceModalResult = {
  scrolledToEnd: Record<string, boolean>
  accepted: Record<string, boolean>
  isSubmitting: boolean
  error: string
  canSubmit: boolean
  handleScroll: (docId: string) => (event: UIEvent<HTMLDivElement>) => void
  handleCheckedChange: (docId: string, value: boolean) => void
  handleSubmit: () => Promise<void>
}

const SCROLL_END_THRESHOLD_PX = 10

export function useLegalAcceptanceModal({
  documents,
  onAccept,
}: UseLegalAcceptanceModalParams): UseLegalAcceptanceModalResult {
  const [scrolledToEnd, setScrolledToEnd] = useState<Record<string, boolean>>({})
  const [accepted, setAccepted] = useState<Record<string, boolean>>({})
  const { isSubmitting, runGuarded } = useSubmitGuard()
  const [error, setError] = useState('')
  const onAcceptRef = useRef(onAccept)
  onAcceptRef.current = onAccept

  const handleScroll = useCallback(
    (docId: string) => (event: UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget
      const distanceFromBottom = target.scrollHeight - target.scrollTop - target.clientHeight
      if (distanceFromBottom >= SCROLL_END_THRESHOLD_PX) return
      setScrolledToEnd((prev) => (prev[docId] ? prev : { ...prev, [docId]: true }))
    },
    [],
  )

  const handleCheckedChange = useCallback((docId: string, value: boolean) => {
    setAccepted((prev) => ({ ...prev, [docId]: value }))
  }, [])

  const canSubmit = useMemo(() => {
    if (documents.length === 0) return false
    if (isSubmitting) return false
    return documents.every(
      (doc) => scrolledToEnd[doc.documentId] === true && accepted[doc.documentId] === true,
    )
  }, [documents, scrolledToEnd, accepted, isSubmitting])

  const handleSubmit = useCallback(async () => {
    if (documents.length === 0) return
    const allReady = documents.every(
      (doc) => scrolledToEnd[doc.documentId] === true && accepted[doc.documentId] === true,
    )
    if (!allReady) return
    setError('')
    await runGuarded(async () => {
      try {
        await onAcceptRef.current(documents)
      } catch {
        setError('Failed to record acceptance. Please check your connection and try again.')
      }
    })
  }, [documents, scrolledToEnd, accepted, runGuarded])

  return {
    scrolledToEnd,
    accepted,
    isSubmitting,
    error,
    canSubmit,
    handleScroll,
    handleCheckedChange,
    handleSubmit,
  }
}
